import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';

import styled from '@emotion/styled';

import { useQuery } from '@apollo/client/react';

import { useAbeetContext } from '@clutter/abeet';

import { DraftDocument, DraftFragment } from '@graphql/platform';

import { useResolveCheckoutURL } from '@root/components/checkout/utilities/checkout_url';
import { generateLeadSource } from '@root/resources/lead';

import { Action, useClientDataContext } from '@shared/client_data_context';
import { ServerDataContext } from '@shared/server_data_context';
import { ServiceSelection } from '@shared/service_selector/constants';

import { getPageName } from '@utils/get_pagename_utilities';
import { enrollStorageBookingFunnelTest } from '@utils/hooks/storage_booking_funnel_test';

import { hasHomeDraft, useDraftResume } from './draft_resume';
import { HomeDesktopOnly, HomeMobileOnly, PrimaryButton } from './styles';
import { MEDIA, TOKENS } from './tokens';

export const HomeResponsiveQuoteLabel: React.FC = () => (
  <>
    <HomeMobileOnly>Get quote</HomeMobileOnly>
    <HomeDesktopOnly>Get a free quote</HomeDesktopOnly>
  </>
);

const QuoteButton = styled(PrimaryButton)`
  padding: ${TOKENS.space[8]} ${TOKENS.space[16]};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 1.2;

  ${MEDIA.lg} {
    padding: ${TOKENS.space[12]} ${TOKENS.space[24]};
    font-size: 16px;
    font-weight: 500;
  }
`;

const useHomeStorageCheckoutEntry = (entryPoint: string) => {
  const history = useHistory();
  const { dispatch } = useClientDataContext();
  const { execute } = useAbeetContext();
  const { isHomepage } = useContext(ServerDataContext);
  const resolveCheckoutURL = useResolveCheckoutURL();

  return async (event?: { preventDefault(): void }) => {
    event?.preventDefault();

    dispatch({
      type: Action.SetupCheckout,
      payload: {
        zipHasSelfStorage: false,
        serviceSelection: ServiceSelection.Storage,
        leadSource: generateLeadSource({
          domain: isHomepage ? 'www' : 'landing',
          pageName: getPageName({ prefix: false }),
          entryPoint,
        }),
      },
    });

    await enrollStorageBookingFunnelTest(execute);
    const url = await resolveCheckoutURL(ServiceSelection.Storage);
    history.push(url);
  };
};

export const HomeQuoteCta: React.FC<{
  className?: string;
  inMobileMenu?: boolean;
  label?: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
  entryPoint?: string;
}> = ({
  className,
  inMobileMenu,
  label,
  onClick,
  entryPoint = 'home_header',
}) => {
  const { data, loading } = useQuery(DraftDocument);
  const draft = data?.draft;
  const enterStorageCheckout = useHomeStorageCheckoutEntry(entryPoint);

  const quoteLabel =
    label ?? (inMobileMenu ? 'Get a free quote' : <HomeResponsiveQuoteLabel />);

  if (!loading && draft && hasHomeDraft(draft)) {
    return (
      <ContinueBookingButton
        draft={draft}
        className={className}
        onClick={onClick}
        entryPoint={entryPoint}
      />
    );
  }

  const handleClick: React.MouseEventHandler<HTMLElement> = async (event) => {
    await enterStorageCheckout(event);
    onClick?.(event);
  };

  return (
    <QuoteButton
      as="button"
      type="button"
      className={className}
      onClick={handleClick}
    >
      {quoteLabel}
    </QuoteButton>
  );
};

const ContinueBookingButton: React.FC<{
  draft: DraftFragment;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
  entryPoint: string;
}> = ({ draft, className, onClick, entryPoint }) => {
  const { path, resume } = useDraftResume(draft, entryPoint);

  const handleClick: React.MouseEventHandler<HTMLElement> = (event) => {
    resume(event);
    onClick?.(event);
  };

  return (
    <QuoteButton href={path} className={className} onClick={handleClick}>
      Continue booking
    </QuoteButton>
  );
};
